import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Input, IconGraphy, colors, Typography, Button } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import {
  verifyEmailAction,
  sendEmailVerificationAction,
  setEmailVerifyAction,
} from 'redux/actions/emailActions';
import { validationMessages } from 'utils/validationMessages';
import useResendCooldown from 'hooks/useResendCooldown';

function OtpPage(props) {
  const { handleBack, target, handleNext } = props;
  const dispatch = useDispatch();
  const verificationStatus = useSelector((state) => state.formData.emailVerify);

  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [key, setKey] = useState(0);

  const { isCoolingDown, canResend, startCooldown, formatTime } = useResendCooldown(target?.email, {
    seconds: 60,
    startOnMount: true,
  });

  useEffect(() => {
    setError(false);
    dispatch(setEmailVerifyAction('none'));
    setKey((prev) => prev + 1);
  }, [dispatch]);

  useEffect(() => {
    if (verificationStatus === 'success') {
      dispatch(setEmailVerifyAction('none'));
      setSending(false);
      setIsVerifying(false);
      handleNext();
    } else if (verificationStatus === 'failed') {
      setSending(false);
      if (isVerifying) {
        setError(true);
      }
      setIsVerifying(false);
    } else if (verificationStatus === 'sent') {
      setSending(false);
      setIsVerifying(false);
    }
  }, [verificationStatus, dispatch, handleNext, isVerifying]);

  const onClickResend = async () => {
    if (!canResend || sending) return;

    startCooldown(60);
    setError(false);
    setSending(true);
    setKey((prev) => prev + 1);

    const result = await dispatch(sendEmailVerificationAction(target));
    setSending(false);

    if (result?.retryAfterSeconds) {
      startCooldown(result.retryAfterSeconds);
    }
  };

  const onChangeOtp = (value) => {
    if (value.length === 6) {
      setError(false);
      setIsVerifying(true);
      setSending(true);
      dispatch(setEmailVerifyAction('none'));
      dispatch(verifyEmailAction({ ...target, code: value }));
    }
  };

  const resendLabel = isCoolingDown ? `Resend Code in ${formatTime()}` : 'Resend Code';

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '16px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          width: 128,
          height: 128,
          borderRadius: '50%',
          background: colors.purple[100],
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <IconGraphy
          icon="Notifications.MarkMailRead"
          width={63}
          height={54}
          style={{ color: colors.purple[700] }}
        />
      </Box>

      <Box>
        <Typography variant="h3" style={{ fontWeight: '500' }}>
          We've sent you a code
        </Typography>

        <Typography variant="body1" style={{ color: colors.neutral[900], marginTop: '12px' }}>
          The code was sent to you via{' '}
          <span style={{ color: colors.purple[700], fontWeight: '500' }}>Email</span> at{' '}
          <span style={{ color: colors.purple[700], fontWeight: '500' }}>{target.email}</span>
        </Typography>
      </Box>

      <Box>
        <Input
          key={key}
          onChange={(e) => onChangeOtp(e.target.value)}
          type="otp"
          size="medium"
          otpLength={6}
          fullWidth
          helperMessage={error ? validationMessages.otp : ''}
          errorType={error ? 'error' : 'none'}
        />
      </Box>

      <Box sx={{ width: '100%', border: `1px solid ${colors.purple[100]}` }} />

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', gap: '16px', mt: 2 }}
      >
        <Button size="medium" variant="tertiary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          disabled={!canResend || sending}
          onClick={onClickResend}
          style={{ flexShrink: 1 }}
          aria-disabled={!canResend || sending}
        >
          {resendLabel}
        </Button>
      </Box>
    </Box>
  );
}

OtpPage.propTypes = {
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  target: PropTypes.shape({
    email: PropTypes.string,
    fname: PropTypes.string,
  }).isRequired,
};

export default OtpPage;
